import React from "react";
import Badge from "../../components/ui/Badge";

interface Props {
  statut: string;
  dateDebut?: string;
  dateFin?: string;
  preuve?: string;
  fichier?: string;
  moyenneMinutes?: number; // 📈 moyenne historique
  seuilAnormal?: number;   // 🧠 seuil mission courte (ex: 10 min)
}

const formatDateTime = (value?: string) => {
  if (!value) return null;
  const d = new Date(value);
  return isNaN(d.getTime())
    ? null
    : d.toLocaleString("fr-FR");
};

const getBadgeVariant = (statut: string) => {
  const s = statut?.toUpperCase();

  if (s === "PLANIFIEE") return "default";
  if (s === "EN_COURS") return "warning";
  if (s === "TERMINEE" || s === "CLOTUREE") return "success";
  if (s === "ANNULEE") return "error";

  return "default";
};

const computeDurationMinutes = (start?: string, end?: string) => {
  if (!start || !end) return null;

  const d1 = new Date(start);
  const d2 = new Date(end);

  if (isNaN(d1.getTime()) || isNaN(d2.getTime())) return null;

  const diffMs = d2.getTime() - d1.getTime();
  if (diffMs <= 0) return null;

  return Math.floor(diffMs / 60000);
};

const formatDuration = (minutes: number) => {
  const hours = Math.floor(minutes / 60);
  const remaining = minutes % 60;

  if (hours > 0) return `${hours}h ${remaining}min`;
  return `${minutes} minutes`;
};

const MissionExecutionCard: React.FC<Props> = ({
  statut,
  dateDebut,
  dateFin,
  preuve,
  fichier,
  moyenneMinutes = 45, // valeur par défaut
  seuilAnormal = 10,
}) => {
  const dureeMinutes = computeDurationMinutes(dateDebut, dateFin);
  const dureeFormatted =
    dureeMinutes !== null ? formatDuration(dureeMinutes) : null;

  const isMissionCourte =
    dureeMinutes !== null && dureeMinutes < seuilAnormal;

  const preuveManquante =
    statut?.toUpperCase() === "CLOTUREE" && !preuve;

  return (
    <div className="bg-slate-900/40 p-5 rounded-xl border border-slate-700 shadow-md space-y-5">
      <h2 className="font-semibold text-base text-gray-200">
        🚀 Exécution
      </h2>

      {/* ============================ */}
      {/* 📌 Statut */}
      {/* ============================ */}
      <div className="flex items-center gap-2">
        <span className="font-medium text-gray-400">
          📌 Statut :
        </span>
        <Badge variant={getBadgeVariant(statut)}>
          {statut}
        </Badge>
      </div>

      {/* ============================ */}
      {/* 📊 Timeline visuelle */}
      {/* ============================ */}
      <div className="relative border-l-2 border-slate-600 pl-4 space-y-4 text-sm text-gray-300">

        {dateDebut && (
          <div>
            <div className="absolute -left-2.5 w-4 h-4 bg-blue-500 rounded-full"></div>
            <div>
              <strong>🚀 Début :</strong>{" "}
              {formatDateTime(dateDebut)}
            </div>
          </div>
        )}

        {dateFin && (
          <div>
            <div className="absolute -left-2.5 w-4 h-4 bg-green-500 rounded-full"></div>
            <div>
              <strong>🏁 Fin :</strong>{" "}
              {formatDateTime(dateFin)}
            </div>
          </div>
        )}
      </div>

      {/* ============================ */}
      {/* ⏳ Durée */}
      {/* ============================ */}
      {dureeFormatted && (
        <div className="text-sm text-gray-300">
          <span className="font-medium text-gray-400">
            ⏳ Durée :
          </span>{" "}
          {dureeFormatted}
        </div>
      )}

      {/* ============================ */}
      {/* 📈 Comparaison moyenne */}
      {/* ============================ */}
      {dureeMinutes !== null && (
        <div className="text-xs text-gray-400">
          📊 Moyenne missions : {formatDuration(moyenneMinutes)}
        </div>
      )}

      {/* ============================ */}
      {/* 🧠 Mission anormalement courte */}
      {/* ============================ */}
      {isMissionCourte && (
        <div className="bg-red-600/20 text-red-400 p-3 rounded text-xs border border-red-600/40">
          ⚠️ Mission anormalement courte (moins de {seuilAnormal} minutes)
        </div>
      )}

      {/* ============================ */}
      {/* 🔐 Preuve obligatoire */}
      {/* ============================ */}
      {preuveManquante && (
        <div className="bg-yellow-600/20 text-yellow-400 p-3 rounded text-xs border border-yellow-600/40">
          🔐 Mission clôturée sans preuve terrain.
        </div>
      )}

      {/* ============================ */}
      {/* 📷 Preuve */}
      {/* ============================ */}
      {preuve && (
        <div className="space-y-2">
          <span className="font-medium text-gray-400">
            📷 Preuve :
          </span>
          <img
            src={preuve}
            alt="Preuve mission"
            className="rounded-lg border border-slate-700 max-h-52 object-cover"
          />
        </div>
      )}

      {/* ============================ */}
      {/* 📄 Rapport PDF */}
      {/* ============================ */}
      {fichier && (
        <div>
          <span className="font-medium text-gray-400">
            📄 Rapport PDF :
          </span>{" "}
          <a
            href={fichier}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block mt-2 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg text-xs transition"
          >
            Télécharger le rapport
          </a>
        </div>
      )}
    </div>
  );
};

export default MissionExecutionCard;